import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Blocks, ExternalLink, Plus, Settings, Webhook } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { queryKeys } from '@/queries/keys';
import slackService from '@/lib/api/slack';
import { toast } from '@/hooks/use-toast';
import webhooksService from '@/lib/api/webhooks';
import SlackConfigDialog from '@/components/integrations/SlackConfigDialog';
import WebhookFormDialog from '@/components/integrations/WebhookFormDialog';

interface CrmIntegration {
  id: string;
  name: string;
  description: string;
  logoUrl: string;
  comingSoon?: boolean;
}

const CRM_INTEGRATIONS: CrmIntegration[] = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync leads and engagement activity to HubSpot CRM. Automatically create contacts and log interactions.',
    logoUrl: 'https://img.logo.dev/hubspot.com?token=pk_a3-E5RCzS9CzxGENt9PrcQ',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Connect with Salesforce to push qualified leads and track engagement activity in your sales pipeline.',
    logoUrl: 'https://img.logo.dev/salesforce.com?token=pk_a3-E5RCzS9CzxGENt9PrcQ',
    comingSoon: true,
  },
  {
    id: 'attio',
    name: 'Attio',
    description: 'Integrate with Attio to enrich lead profiles and sync engagement insights to your collaborative CRM.',
    logoUrl: 'https://img.logo.dev/attio.com?token=pk_a3-E5RCzS9CzxGENt9PrcQ',
  },
];

const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.id;

  const [slackDialogOpen, setSlackDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const slackStatus = searchParams.get('slack');
    const error = searchParams.get('error');

    if (slackStatus === 'connected') {
      toast({ title: 'Slack connected', description: 'Your Slack workspace has been successfully connected.' });
      searchParams.delete('slack');
      setSearchParams(searchParams, { replace: true });
    } else if (error) {
      toast({ title: 'Slack connection failed', description: `Could not connect Slack: ${error}`, variant: 'destructive' });
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const slackQuery = useQuery({
    queryKey: organizationId ? queryKeys.slack.config(organizationId) : ['slack', 'config', 'no-org'],
    enabled: !!organizationId,
    queryFn: async () => {
      const response = await slackService.getConfig(organizationId!);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load Slack config.');
      }
      return response.data;
    },
  });

  const webhooksQuery = useQuery({
    queryKey: organizationId ? queryKeys.webhooks.list(organizationId) : ['webhooks', 'list', 'no-org'],
    enabled: !!organizationId,
    queryFn: async () => {
      const response = await webhooksService.listWebhooks(organizationId!);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load webhooks.');
      }
      return response.data.webhooks;
    },
  });

  const slackConnected = slackQuery.data?.connected ?? false;
  const webhookCount = webhooksQuery.data?.length ?? 0;

  return (
    <>
      <Helmet>
        <title>Integrations | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader title="Integrations" />

        <div className="flex-1">
          <div className="px-4 sm:px-6 md:px-8 pb-8 pt-6">
            <div className="space-y-8">
              {/* Communication */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Communication</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="relative overflow-hidden border card-border shadow-none hover:shadow-md hover:border-primary/20 group transition-all">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src="https://img.logo.dev/slack.com?token=pk_a3-E5RCzS9CzxGENt9PrcQ"
                            alt="Slack logo"
                            className="w-8 h-8 rounded-lg object-contain shrink-0"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 group-hover:text-primary transition-colors">
                              Slack
                              {slackQuery.isLoading ? (
                                <Badge variant="secondary" className="text-xs">Loading...</Badge>
                              ) : slackConnected ? (
                                <Badge variant="default" className="bg-success/10 text-success border-success/20 hover:bg-success/10">Connected</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Not connected</Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="mt-1 line-clamp-2">
                              Get real-time notifications about leads, engagement, and buying signals in Slack.
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-end pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 h-8"
                          onClick={() => setSlackDialogOpen(true)}
                        >
                          <Settings className="h-3 w-3" /> Configure
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* CRM */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">CRM</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CRM_INTEGRATIONS.map((crm) => (
                    <Card
                      key={crm.id}
                      className={`relative overflow-hidden border card-border shadow-none transition-all ${
                        crm.comingSoon
                          ? 'opacity-60'
                          : 'hover:shadow-md hover:border-primary/20 group'
                      }`}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <img
                              src={crm.logoUrl}
                              alt={`${crm.name} logo`}
                              className="w-8 h-8 rounded-lg object-contain shrink-0"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <div>
                              <CardTitle className={`text-lg flex items-center gap-2 ${!crm.comingSoon ? 'group-hover:text-primary' : ''} transition-colors`}>
                                {crm.name}
                                {crm.comingSoon && (
                                  <Badge variant="secondary" className="bg-muted text-muted-foreground border-border">
                                    Coming soon
                                  </Badge>
                                )}
                              </CardTitle>
                              <CardDescription className="mt-1 line-clamp-2">
                                {crm.description}
                              </CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-end pt-2">
                          {!crm.comingSoon && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2 h-8"
                              onClick={() => navigate(`/integrations/${crm.id}`)}
                            >
                              Set up
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Custom Integrations */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Custom Integrations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="relative overflow-hidden border card-border shadow-none hover:shadow-md hover:border-primary/20 group transition-all">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            <Webhook className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 group-hover:text-primary transition-colors">
                              Webhooks
                              {webhookCount > 0 && (
                                <Badge variant="secondary" className="text-xs">{webhookCount}</Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="mt-1 line-clamp-2">
                              Send event data to any HTTP endpoint. Manage all your webhook configurations.
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 h-8"
                          onClick={() => setWebhookDialogOpen(true)}
                        >
                          <Plus className="h-3 w-3" /> Create New
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 h-8"
                          onClick={() => navigate('/integrations/webhooks')}
                        >
                          Manage
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {organizationId && (
        <>
          <SlackConfigDialog
            open={slackDialogOpen}
            onOpenChange={setSlackDialogOpen}
            organizationId={organizationId}
          />
          <WebhookFormDialog
            open={webhookDialogOpen}
            onOpenChange={setWebhookDialogOpen}
            organizationId={organizationId}
          />
        </>
      )}
    </>
  );
};

export default IntegrationsPage;
